import React, { useState, useRef, useEffect } from "react";
import Step1 from "./Step1.jsx";
import Step2 from "./Step2.jsx";
import Step3 from "./Step3.jsx";
import ConfirmDialog from "./ConfirmDialog";
import Success from "./Success";

const TABS = ["Hluti 1", "Hluti 2", "Hluti 3"];

const initialData = {
  full_name: "",
  ssn: "",
  phone_number: "",
  email: "",
  place: "",
  next_of_kin: "",
  phone_number_next_of_kin: "",
  referred_by: "",
  has_completed_orientation: false,
  started_at_hugarafl: "",
  accepts_criminal_record_check: false,
  gender: "",
  username: "",
  accepts_house_rules: false,
  accepts_confidentiality_agreement: false,
  // NEW: boolean informed consent
  informed_consent: false,
};

/* ---------- helpers ---------- */
const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");
const normalizeSSN = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  return d.length >= 7 ? d.slice(0, 6) + "-" + d.slice(6) : d;
};
const isSSNValid = (v) => /^\d{6}-?\d{4}$/.test(v || "");
const normalizePhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 7);
  return d.length === 7 ? d.slice(0, 3) + "-" + d.slice(3) : d;
};

/* ---------- validators ---------- */
function getErrorsStep0(d, usernameAvailability) {
  const e = {};
  const ssn = normalizeSSN(d.ssn);
  const phone = normalizePhone(d.phone_number);
  const nokPhone = normalizePhone(d.phone_number_next_of_kin);

  if (!d.full_name) e.full_name = "Vantar fullt nafn";
  if (!validateEmail(d.email)) e.email = "Ógilt netfang";
  if (!isSSNValid(ssn)) e.ssn = "Kennitala þarf að vera 123456-7890";
  if (normalizePhone(phone).length !== 8) e.phone_number = "Sími þarf að vera 555-5555";
  if (!d.place) e.place = "Vantar sveitarfélag";
  if (!d.username) e.username = "Vantar notendanafn";

  if (!d.next_of_kin) e.next_of_kin = "Vantar nafn nánasta aðstandanda";
  if (nokPhone.length !== 8) e.phone_number_next_of_kin = "Sími aðstandanda þarf að vera 555-5555";

  if (!d.referred_by) e.referred_by = "Vantar hver vísar þér";
  if (!d.gender) e.gender = "Vinsamlegast veldu kyn";

  // require join date and disallow future
  if (!d.started_at_hugarafl) {
    e.started_at_hugarafl = "Vinsamlegast veldu dagsetningu";
  } else {
    const today = new Date().toISOString().slice(0, 10);
    if (d.started_at_hugarafl > today) {
      e.started_at_hugarafl = "Dagsetning má ekki vera í framtíðinni";
    }
  }

  // Must explicitly accept the criminal record check
  if (d.accepts_criminal_record_check !== true) {
    e.accepts_criminal_record_check = "Þú þarft að samþykkja þetta skilyrði til að halda áfram";
  }

  if (usernameAvailability === false) e.username = "Notendanafn er frátekið";
  return e;
}

function getErrorsStep1(d) {
  const e = {};
  if (!d.accepts_house_rules) e.accepts_house_rules = "Þú þarft að samþykkja húsreglurnar";
  return e;
}

function getErrorsStep2(d) {
  const e = {};
  if (!d.accepts_confidentiality_agreement)
    e.accepts_confidentiality_agreement = "Þú þarft að samþykkja trúnaðaryfirlýsingu";
  // informed consent required (checkbox)
  if (d.informed_consent !== true)
    e.informed_consent = "Þú þarft að staðfesta upplýst samþykki";
  return e;
}

function getErrorsForStepIndex(idx, d, ua) {
  if (idx === 0) return getErrorsStep0(d, ua);
  if (idx === 1) return getErrorsStep1(d);
  return getErrorsStep2(d);
}

// map fields -> step index to focus the right tab on error
const stepOfField = (f) => {
  const s0 = new Set([
    "full_name",
    "ssn",
    "phone_number",
    "email",
    "place",
    "next_of_kin",
    "phone_number_next_of_kin",
    "referred_by",
    "started_at_hugarafl",
    "gender",
    "username",
    "accepts_criminal_record_check",
  ]);
  const s1 = new Set(["accepts_house_rules", "has_completed_orientation"]);
  const s2 = new Set(["accepts_confidentiality_agreement", "informed_consent"]);
  if (s0.has(f)) return 0;
  if (s1.has(f)) return 1;
  if (s2.has(f)) return 2;
  return 0;
};

export default function Wizard() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);

  // username check state
  const [usernameAvailability, setUsernameAvailability] = useState(null); // true/false/null
  const [lastCheckedUsername, setLastCheckedUsername] = useState("");
  const usernameReqId = useRef(0);

  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = showConfirm ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showConfirm]);

  /* ----- username helpers ----- */
  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const { [field]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const clearUsernameCheck = () => {
    usernameReqId.current += 1; // invalidate any in-flight checks
    setUsernameAvailability(null);
    setLastCheckedUsername("");
    clearFieldError("username");
  };

  const checkUsername = async (username) => {
    const value = (username || "").trim();
    setLastCheckedUsername(value);
    if (!value) {
      clearUsernameCheck();
      return null;
    }

    const myId = ++usernameReqId.current;
    try {
      const resp = await fetch(
        `/api/username-availability/?username=${encodeURIComponent(value)}`,
        { method: "GET", credentials: "omit" } // public endpoint, no auth/cookies
      );

      // Treat non-200 as unknown; don't block the form on transient errors
      if (!resp.ok) {
        if (usernameReqId.current === myId) setUsernameAvailability(null);
        return null;
      }

      const body = await resp.json();

      // stale response guard
      const current = (data.username || "").trim();
      if (usernameReqId.current !== myId || value !== current) return null;

      const available = typeof body?.available === "boolean" ? body.available : null;
      if (available === null) {
        setUsernameAvailability(null);
        clearFieldError("username");
        return null;
      }
      setUsernameAvailability(available);
      available
        ? clearFieldError("username")
        : setErrors((e) => ({ ...e, username: "Notendanafn er frátekið" }));
      return available;
    } catch {
      if (usernameReqId.current === myId) setUsernameAvailability(null);
      return null;
    }
  };

  /* ----- navigation ----- */
  const next = async () => {
    let currentData = data;
    if (step === 0) {
      const normalized = {
        ...data,
        ssn: normalizeSSN(data.ssn),
        phone_number: normalizePhone(data.phone_number),
        phone_number_next_of_kin: normalizePhone(data.phone_number_next_of_kin),
      };
      currentData = normalized;
      setData(normalized);

      if (normalized.username && usernameAvailability !== true) {
        await checkUsername(normalized.username);
      }
    }

    const e = getErrorsForStepIndex(step, currentData, usernameAvailability);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setErrors({});
    if (step === 0) clearUsernameCheck();
    setStep((s) => Math.min(s + 1, 2));
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleTabClick = async (targetIdx) => {
    if (targetIdx <= step) {
      setErrors({});
      setStep(targetIdx);
      return;
    }

    let workingData = data;
    for (let i = 0; i < targetIdx; i++) {
      if (i === 0) {
        const normalized = {
          ...workingData,
          ssn: normalizeSSN(workingData.ssn),
          phone_number: normalizePhone(workingData.phone_number),
          phone_number_next_of_kin: normalizePhone(workingData.phone_number_next_of_kin),
        };
        workingData = normalized;

        if (normalized.username && usernameAvailability !== true) {
          await checkUsername(normalized.username);
        }
      }
      const e = getErrorsForStepIndex(i, workingData, usernameAvailability);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        if (i > 0) setData(workingData);
        return;
      }
    }
    if (step === 0) clearUsernameCheck();
    setErrors({});
    setData(workingData);
    setStep(targetIdx);
  };

  const isStepUnlocked = (idx) => {
    if (idx === 0) return true;
    let workingData = data;
    for (let i = 0; i < idx; i++) {
      if (i === 0) {
        workingData = {
          ...workingData,
          ssn: normalizeSSN(workingData.ssn),
          phone_number: normalizePhone(workingData.phone_number),
          phone_number_next_of_kin: normalizePhone(workingData.phone_number_next_of_kin),
        };
      }
      if (Object.keys(getErrorsForStepIndex(i, workingData, usernameAvailability)).length) return false;
    }
    return true;
  };

  /* ----- submit ----- */
  const onSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    // Full client-side validation across all steps (normalize step 0)
    const normalized0 = {
      ...data,
      ssn: normalizeSSN(data.ssn),
      phone_number: normalizePhone(data.phone_number),
      phone_number_next_of_kin: normalizePhone(data.phone_number_next_of_kin),
    };
    const e0 = getErrorsStep0(normalized0, usernameAvailability);
    const e1 = getErrorsStep1(normalized0);
    const e2 = getErrorsStep2(normalized0);
    const allErrors = { ...e0, ...e1, ...e2 };
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      const firstField = Object.keys(allErrors)[0];
      setStep(stepOfField(firstField));
      setSubmitting(false);
      setShowConfirm(false);
      return;
    }

    try {
      // PUBLIC submit (no Authorization/cookies) to avoid 401 on AllowAny
      const resp = await fetch("/api/pending-users/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(normalized0),
      });
      if (!resp.ok) {
        let body;
        try { body = await resp.json(); } catch { body = await resp.text(); }
        const err = { response: { data: body } };
        throw err;
      }
      setDone(true);
    } catch (e) {
      const server = e?.response?.data;
      if (server && typeof server === "object") {
        setErrors(server);
        const firstField = Object.keys(server)[0];
        if (firstField) setStep(stepOfField(firstField));
      } else {
        setSubmitError("Upp kom villa. Reyndu aftur.");
      }
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  if (done) return <Success />;

  const endDisabled = Object.keys(getErrorsStep2(data)).length > 0;

  return (
    <div className="page">
      <header className="banner">
        <div className="brand brand-static" aria-label="Hugarafl">
          <div className="logo" />
          <div className="title">Hugarafl Skráning</div>
        </div>
      </header>

      <main className="content">
        <h1 className="page-title">Félagsaðild</h1>

        <section className="card">
          {/* … intro text unchanged … */}

          <nav className="tabs" role="tablist" aria-label="Skráningar skref">
            {TABS.map((t, i) => {
              const unlocked = isStepUnlocked(i);
              const disabled = !unlocked && i > step;
              return (
                <button
                  key={t}
                  className={`tab ${i === step ? "active" : ""} ${disabled ? "disabled" : ""}`}
                  onClick={() => handleTabClick(i)}
                  type="button"
                  disabled={disabled}
                  role="tab"
                  aria-selected={i === step}
                >
                  {t}
                </button>
              );
            })}
          </nav>

          <div className="step-body">
            {step === 0 && (
              <Step1
                data={data}
                setData={(updater) => {
                  if (typeof updater === "function") {
                    setData((prev) => {
                      const next = updater(prev);
                      if ((prev.username || "") !== (next.username || "")) {
                        clearUsernameCheck();
                      }
                      return next;
                    });
                  } else {
                    if ((data.username || "") !== (updater.username || "")) {
                      clearUsernameCheck();
                    }
                    setData(updater);
                  }
                }}
                errors={errors}
                error={errors}
                usernameAvailability={usernameAvailability}
                lastCheckedUsername={lastCheckedUsername}
                checkUsername={checkUsername}
                clearUsernameCheck={clearUsernameCheck}
              />
            )}
            {step === 1 && <Step2 data={data} setData={setData} errors={errors} error={errors} />}
            {step === 2 && <Step3 data={data} setData={setData} errors={errors} error={errors} />}
          </div>

          {submitError ? <div className="error">{submitError}</div> : null}

          <div className="actions">
            {step > 0 ? (
              <button className="ghost" onClick={back} type="button">
                Til baka
              </button>
            ) : (
              <span />
            )}
            {step < 2 ? (
              <button className="primary" onClick={next} type="button">
                Halda áfram
              </button>
            ) : (
              <button
                className="primary"
                onClick={() => setShowConfirm(true)}
                type="button"
                disabled={endDisabled}
                title={endDisabled ? "Ljúktu við þennan hluta fyrst" : undefined}
              >
                Klára
              </button>
            )}
          </div>
        </section>
      </main>

      {showConfirm && (
        <ConfirmDialog onCancel={() => setShowConfirm(false)} onConfirm={onSubmit} loading={submitting} />
      )}

      <footer className="footnote">Allt merkt með * þarf að fylla</footer>
    </div>
  );
}